import React from 'react';
import * as FiIcons from 'react-icons/fi';
import Sparkline from '@/components/charts/Sparkline.jsx';

const TONE_TOKENS = {
  emerald: 'var(--status-running)',
  amber: 'var(--status-queued)',
  crimson: 'var(--status-failed)',
  violet: 'var(--status-autonomous)',
  info: 'var(--status-info)',
  muted: 'var(--text-secondary-ops)'
};

const DIRECTION_TONES = {
  up: 'emerald',
  down: 'crimson',
  flat: 'muted'
};

const DIRECTION_GLYPHS = {
  up: <FiIcons.FiTrendingUp />,
  down: <FiIcons.FiTrendingDown />,
  flat: <FiIcons.FiMinus />
};

/**
 * KPI stat card (T2a).
 *
 * props:
 * - label: string
 * - value: ReactNode — pre-formatted headline figure
 * - delta?: { value: ReactNode, direction: 'up' | 'down' | 'flat' }
 *   (direction color can be overridden with `tone`)
 * - icon?: component — react-icons component rendered next to the label
 * - sparkData?: number[] — renders a Sparkline when provided
 * - tone?: key of TONE_TOKENS — recolors icon + delta (e.g. forced 'muted')
 * - onClick?: () => void — renders the card as a button
 */
function KPICard({
  label,
  value,
  delta = null,
  icon: Icon = null,
  sparkData = null,
  tone = null,
  onClick = null
}) {
  const accentToken = TONE_TOKENS[tone] || TONE_TOKENS.muted;
  const deltaTone = tone || DIRECTION_TONES[delta?.direction] || 'muted';
  const deltaColor = TONE_TOKENS[deltaTone] || TONE_TOKENS.muted;

  const body = (
    <>
      <span className="kpi-card-label">
        {Icon ? <Icon size={13} style={{ color: accentToken }} /> : null}
        {label}
      </span>
      <strong className="kpi-card-value">{value}</strong>
      {delta ? (
        <em className="kpi-card-delta" style={{ color: deltaColor }}>
          {DIRECTION_GLYPHS[delta?.direction] || DIRECTION_GLYPHS.flat}
          {delta.value}
        </em>
      ) : null}
      {Array.isArray(sparkData) && sparkData.length > 0 ? (
        <Sparkline data={sparkData} height={30} />
      ) : null}
    </>
  );

  if (typeof onClick === 'function') {
    return (
      <button className="kpi-card kpi-card-clickable" onClick={onClick} type="button">
        {body}
      </button>
    );
  }

  return <div className="kpi-card">{body}</div>;
}

export default KPICard;
